import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";

const TOKEN_KEY = "tylegn_admin_token";
const ADMIN_KEY = "tylegn_admin_user";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [admin, setAdmin] = useState(() => {
    try {
      const raw = localStorage.getItem(ADMIN_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [verifying, setVerifying] = useState(Boolean(localStorage.getItem(TOKEN_KEY)));

  // Validate stored token on first mount
  useEffect(() => {
    let cancelled = false;
    const verify = async () => {
      const stored = localStorage.getItem(TOKEN_KEY);
      if (!stored) {
        setVerifying(false);
        return;
      }
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/me`, {
          headers: { Authorization: `Bearer ${stored}` },
        });
        if (!cancelled) setAdmin(res.data);
      } catch {
        if (!cancelled) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(ADMIN_KEY);
          setToken(null);
          setAdmin(null);
        }
      } finally {
        if (!cancelled) setVerifying(false);
      }
    };
    verify();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${BACKEND_URL}/api/admin/login`, { email, password });
    const { access_token, admin: adminInfo } = res.data;
    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(ADMIN_KEY, JSON.stringify(adminInfo));
    setToken(access_token);
    setAdmin(adminInfo);
    return adminInfo;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ADMIN_KEY);
    setToken(null);
    setAdmin(null);
  }, []);

  // Centralised admin fetch helper. Automatically attaches the token and
  // triggers logout on 401 so protected pages auto-redirect.
  const apiFetch = useCallback(
    async (method, path, body) => {
      const current = localStorage.getItem(TOKEN_KEY);
      const url = path.startsWith("http") ? path : `${BACKEND_URL}${path}`;
      try {
        const res = await axios({
          method,
          url,
          data: body,
          headers: current ? { Authorization: `Bearer ${current}` } : {},
        });
        return res.data;
      } catch (err) {
        if (err?.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(ADMIN_KEY);
          setToken(null);
          setAdmin(null);
        }
        throw err;
      }
    },
    []
  );

  const value = useMemo(
    () => ({
      token,
      admin,
      verifying,
      isAuthenticated: Boolean(token && admin),
      login,
      logout,
      apiFetch,
    }),
    [token, admin, verifying, login, logout, apiFetch]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};
